import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import {
  FaBold,
  FaItalic,
  FaUnderline,
  FaListUl,
  FaLink,
} from 'react-icons/fa';
import Cookies from 'js-cookie';
import '../styles/EmailSignatureModal.css';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import DrawableSignaturePad from './DrawableSignaturePad';
import {
  buildDefaultSignatureHtml,
  buildDrawnSignatureImgHtml,
  getLocalEmailSignature,
  isBlankSignatureHtml,
  setLocalEmailSignature,
} from './emailSignatureStorage';

const GET_MY_EMAIL_SIGNATURE = `
  query GetMyEmailSignature {
    getMyEmailSignature
  }
`;

const GET_MY_PROFILE = `
  query GetMyProfileForSignature {
    getMyProfile {
      id
      firstName
      lastName
      email
      contact
      jobTitle
      company {
        companyName
      }
    }
  }
`;

const UPDATE_MY_EMAIL_SIGNATURE = `
  mutation UpdateMyEmailSignature($signature: String!) {
    updateMyEmailSignature(signature: $signature) {
      id
      emailSignature
    }
  }
`;

const DRAWN_IMG_REGEX =
  /<img\b[^>]*data-sas-drawn-signature=["']?1["']?[^>]*>/gi;

function stripDrawnFromHtml(html) {
  return String(html || '').replace(DRAWN_IMG_REGEX, '');
}

function extractDrawnFromHtml(html) {
  const matches = String(html || '').match(DRAWN_IMG_REGEX);
  return matches ? matches.join('') : '';
}

function combineSignature(textHtml, drawnHtml) {
  const t = String(textHtml || '');
  const d = String(drawnHtml || '');
  if (!t.trim()) return d;
  if (!d.trim()) return t;
  return `${t}<br><br>${d}`;
}

function normalizePreviewHtml(html) {
  return String(html || '').replace(/(<br\s*\/?>|\s)+$/gi, '').trim();
}

export default function EmailSignatureModal({ onClose, onSaved }) {
  const [signature, setSignature] = useState('');
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState('text'); // 'text' | 'draw'
  const editorRef = useRef(null);
  const [activeFormats, setActiveFormats] = useState({
    bold: false,
    italic: false,
    underline: false,
  });
  const textPreviewHtml = normalizePreviewHtml(stripDrawnFromHtml(signature));
  const drawnPreviewHtml = normalizePreviewHtml(extractDrawnFromHtml(signature));

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      const userId = Cookies.get('userId');
      let html = getLocalEmailSignature(userId);

      try {
        const sigData = await graphqlRequest(GET_MY_EMAIL_SIGNATURE);
        const remoteSignature = sigData?.getMyEmailSignature;
        if (remoteSignature && !isBlankSignatureHtml(remoteSignature)) {
          html = remoteSignature;
          setLocalEmailSignature(html, userId);
        }

        if (isBlankSignatureHtml(html)) {
          const data = await graphqlRequest(GET_MY_PROFILE);
          const profile = data?.getMyProfile;
          if (cancelled) return;
          html = buildDefaultSignatureHtml({
            firstName: profile?.firstName,
            lastName: profile?.lastName,
            jobTitle: profile?.jobTitle,
            email: profile?.email,
            contact: profile?.contact,
            companyName: profile?.company?.companyName,
          });
          if (profile?.id) {
            setLocalEmailSignature(html, profile.id || userId);
          }
        }
      } catch (error) {
        console.warn('Failed to load signature from profile', error);
      }

      if (cancelled) return;
      setSignature(html || '');
      requestAnimationFrame(() => {
        if (editorRef.current) {
          // Keep the text editor clean: drawn signatures should only be visible
          // in the Draw tab / email preview, not in the text editor.
          editorRef.current.innerHTML = stripDrawnFromHtml(html || '');
          const el = editorRef.current;
          const range = document.createRange();
          range.selectNodeContents(el);
          range.collapse(false);
          const sel = window.getSelection();
          sel?.removeAllRanges();
          sel?.addRange(range);
        }
      });
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const syncEditorToState = () => {
    if (editorRef.current) {
      const drawnHtml = extractDrawnFromHtml(signature);
      const textOnly = stripDrawnFromHtml(editorRef.current.innerHTML);
      setSignature(combineSignature(textOnly, drawnHtml));
    }
  };

  const handleSave = async () => {
    const signatureHTML = signature || '';
    if (isBlankSignatureHtml(signatureHTML)) {
      toast.error('Please enter text or draw a signature before saving.');
      return;
    }

    try {
      setSaving(true);
      const userId = Cookies.get('userId');
      setLocalEmailSignature(signatureHTML, userId);
      setSignature(signatureHTML);

      try {
        await graphqlRequest(UPDATE_MY_EMAIL_SIGNATURE, {
          signature: signatureHTML,
        });
      } catch (err) {
        console.error('Failed to sync signature to profile', err);
      }

      onSaved?.(signatureHTML);
      toast.success('Email signature saved. It will be reused for your emails.');
      onClose();
    } catch (error) {
      console.error('Error saving signature:', error);
      toast.error('Failed to save signature. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleEditorChange = () => syncEditorToState();

  const toggleFormat = (command, formatKey) => {
    if (!editorRef.current) return;
    editorRef.current.focus();
    document.execCommand(command, false, null);
    syncEditorToState();
    if (formatKey) {
      setActiveFormats((prev) => ({
        ...prev,
        [formatKey]: !prev[formatKey],
      }));
    }
  };

  const handleInsertLink = () => {
    if (!editorRef.current) return;
    const url = prompt('Enter the link URL:');
    if (!url) return;
    const text = prompt('Enter link text:', url) || url;
    editorRef.current.focus();
    document.execCommand('createLink', false, url);
    const selection = window.getSelection();
    if (selection?.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const linkElement = range.commonAncestorContainer.parentElement;
      if (linkElement?.tagName === 'A') {
        linkElement.textContent = text;
      }
    }
    syncEditorToState();
  };

  const handleInsertDrawn = (dataUrl) => {
    const imgHtml = buildDrawnSignatureImgHtml(dataUrl);
    if (!imgHtml) return;

    const textOnly = stripDrawnFromHtml(signature);
    setSignature(combineSignature(textOnly, imgHtml));

    // Keep the text editor clean.
    if (editorRef.current) editorRef.current.innerHTML = textOnly;
    setMode('draw');
  };

  const handleClearDrawn = () => {
    const textOnly = stripDrawnFromHtml(signature);
    setSignature(textOnly);
    if (editorRef.current) editorRef.current.innerHTML = textOnly;
  };

  // Only rehydrate the text editor when switching back to the Text tab.
  // Do NOT depend on `signature` — rewriting innerHTML on every keystroke
  // resets the caret to the start.
  useEffect(() => {
    if (mode !== 'text' || !editorRef.current) return;
    const textOnly = stripDrawnFromHtml(signature);
    if (editorRef.current.innerHTML === textOnly) return;
    editorRef.current.innerHTML = textOnly;

    // Place caret at the end so typing continues from the last position.
    const el = editorRef.current;
    const range = document.createRange();
    range.selectNodeContents(el);
    range.collapse(false);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only on mode change
  }, [mode]);

  const handleOverlayClick = (e) => {
    if (e.target.classList.contains('email-signature-overlay')) {
      onClose();
    }
  };

  return (
    <div className="email-signature-overlay" onClick={handleOverlayClick}>
      <div className="email-signature-modal">
        <div className="email-signature-header">
          <span className="email-signature-title">Email Signature</span>
          <button
            type="button"
            className="email-signature-close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="email-signature-body">
          <p style={{ margin: '0 0 12px', color: '#64748b', fontSize: 13 }}>
            Save once and reuse this signature on every email you send. You can
            type details and add a drawn signature.
          </p>

          <div className="email-signature-mode-tabs">
            <button
              type="button"
              className={`email-signature-mode-tab ${mode === 'text' ? 'active' : ''}`}
              onClick={() => setMode('text')}
            >
              Text details
            </button>
            <button
              type="button"
              className={`email-signature-mode-tab ${mode === 'draw' ? 'active' : ''}`}
              onClick={() => setMode('draw')}
            >
              Draw signature
            </button>
          </div>

          <div className="email-signature-editor">
            {mode === 'draw' ? (
              <DrawableSignaturePad
                onInsert={handleInsertDrawn}
                onClear={handleClearDrawn}
              />
            ) : (
              <div className="email-signature-toolbar">
                <button
                  type="button"
                  className={`email-signature-toolbar-btn ${activeFormats.bold ? 'active' : ''}`}
                  title="Bold"
                  onClick={() => toggleFormat('bold', 'bold')}
                >
                  <FaBold />
                </button>
                <button
                  type="button"
                  className={`email-signature-toolbar-btn ${activeFormats.italic ? 'active' : ''}`}
                  title="Italic"
                  onClick={() => toggleFormat('italic', 'italic')}
                >
                  <FaItalic />
                </button>
                <button
                  type="button"
                  className={`email-signature-toolbar-btn ${activeFormats.underline ? 'active' : ''}`}
                  title="Underline"
                  onClick={() => toggleFormat('underline', 'underline')}
                >
                  <FaUnderline />
                </button>
                <button
                  type="button"
                  className="email-signature-toolbar-btn"
                  title="Bullet List"
                  onClick={() => toggleFormat('insertUnorderedList', null)}
                >
                  <FaListUl />
                </button>
                <button
                  type="button"
                  className="email-signature-toolbar-btn"
                  title="Insert Link"
                  onClick={handleInsertLink}
                >
                  <FaLink />
                </button>
              </div>
            )}

            <div
              ref={editorRef}
              contentEditable
              suppressContentEditableWarning
              className={`email-signature-textarea${mode === 'draw' ? ' email-signature-textarea-hidden' : ''}`}
              onInput={handleEditorChange}
              data-placeholder="Enter your email signature here..."
            />

            <div className="email-signature-preview">
              <label className="email-signature-preview-label">Signature Preview</label>
              <div className="email-signature-preview-content">
                <div
                  style={{
                    fontWeight: 900,
                    color: '#111827',
                    marginBottom: 6,
                  }}
                >
                  Text
                </div>
                <div
                  dangerouslySetInnerHTML={{
                    __html:
                      textPreviewHtml ||
                      '<span style="color:#9ca3af">No text signature written yet</span>',
                  }}
                />

                <div
                  style={{
                    height: 1,
                    background: '#e5e7eb',
                    margin: '14px 0',
                  }}
                />

                <div
                  style={{
                    fontWeight: 900,
                    color: '#111827',
                    marginBottom: 6,
                  }}
                >
                  Drawn
                </div>
                <div
                  dangerouslySetInnerHTML={{
                    __html:
                      drawnPreviewHtml ||
                      '<span style="color:#9ca3af">No drawn signature saved yet</span>',
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="email-signature-footer">
          <button
            type="button"
            className="email-signature-cancel-btn"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="email-signature-save-btn"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save Signature'}
          </button>
        </div>
      </div>
    </div>
  );
}

export { getLocalEmailSignature as getEmailSignature } from './emailSignatureStorage';
